'use client'
import Select, {components as selectComponents} from "react-select";
import {useEffect, useState} from "react";
import {FilterVariant, IAppliedFilter, ISelectFilterConfig} from "@/app/models/auctor";
import dynamic from "next/dynamic";

const AppIcon = dynamic(() => import('@/app/components/atomic/AppIcon'));

function OptionWithIcon({data, ...props}: any) {
    const className = 'text-pale hover:bg-rm-gray/40 transition-all cursor-pointer'
    return (
        <selectComponents.Option className={className} {...props}>
            {data.icon !== null && data.icon !== '' &&
                <AppIcon className={"mr-2"} iconClassName={data.icon} />
            }
            {data.label}
        </selectComponents.Option>
    );
}

function DropdownIndicator({state, ...props}: any) {
    const isOpenClass = props.selectProps.menuIsOpen ? 'rotate-180' : ''
    const colorClass = 'text-pale'
    const iconClass = 'fa-regular fa-chevron-down'
    return (
        <selectComponents.DropdownIndicator {...props}>
            <AppIcon iconClassName={`${iconClass} ${colorClass} transition-all origin-center cursor-pointer ${isOpenClass}`} />
        </selectComponents.DropdownIndicator>
    )
}

function SingleValue({data, ...props}: any) {
    const colorClass = 'text-pale'
    return (
        <selectComponents.SingleValue className={`${colorClass} cursor-pointer`} {...props}>
            {data.icon !== null && data.icon !== '' &&
                <AppIcon className={"mr-2"} iconClassName={data.icon} />
            }
            {data.label}
        </selectComponents.SingleValue>
    )
}

export default function FilterSelect({filterConfig, appliedFilter, handleChange, variant = "default"}: {
    filterConfig: ISelectFilterConfig,
    appliedFilter: IAppliedFilter,
    handleChange: any,
    variant?: FilterVariant,
}) {
    const id = Date.now().toString();
    const [isMounted, setIsMounted] = useState(false);

    // Must be deleted once
    // https://github.com/JedWatson/react-select/issues/5459 is fixed.
    useEffect(() => setIsMounted(true), []);

    const onChange = (newValue: any) => {
        handleChange(appliedFilter, newValue);
    }

    const theme = {
        control: `focus-within:border-rm-gray hover:border-rm-gray shadow-none h-12 border-rm-gray-light transition-all duration-500 rounded-lg ${variant === "tool" ? "bg-rm-gray-rule" : ""}`,
        option: "bg-rm-gray-light font-semibold",
    }

    return isMounted ? (
        <>
            <Select
                id={id}
                className="w-full cursor-pointer"
                data-test={`filter-select-${filterConfig.variable_name}`}
                classNames={{
                    control: (state) => theme.control,
                    indicatorSeparator: (state) => 'hidden',
                    option: (state) => state.isSelected ? theme.option : 'bg-white',
                    multiValue: (state) => 'bg-blue-200 font-semibold rounded'
                }}
                placeholder={'Sélectionnez...'}
                options={filterConfig.options}
                defaultValue={appliedFilter.value}
                isMulti={filterConfig.is_multiple}
                blurInputOnSelect={true}
                isSearchable={false}
                isClearable={false}
                components={{
                    Option: OptionWithIcon,
                    DropdownIndicator,
                    SingleValue,
                }}
                noOptionsMessage={() => 'Aucune option disponible'}
                onChange={onChange}
                menuPortalTarget={document.body}
                styles={{
                    menuPortal: base => ({...base, zIndex: 20}),
                }}
            />
        </>
    ) :  <HTMLSelectSkeleton options={filterConfig.options} defaultValue={appliedFilter.value} />
}

export function SelectFilterSkeleton() {
    return (
        <>
            <div className="w-full">
                <div className="w-full h-[42px] bg-blue-200 rounded animate-pulse" />
            </div>
        </>
    )
}

export function HTMLSelectSkeleton({options, defaultValue}: {options?: any[], defaultValue: string}) {
    return (
        <select
            className="w-full h-12 px-3 py-2 text-blue-900 bg-white border border-blue-300 rounded-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            defaultValue={defaultValue}
        >
            {options?.map((option: any, index: number) => (
                <option key={`${option.value}-${index}`} value={option.value}>
                    {option.label}
                </option>
            ))}
        </select>
    )
}
